import { listLadBlocks } from '@realplc/lad-core';
import { containsLadderBlocks } from '@realplc/types';
import { highlightSTCode, highlightSTComment } from './stHighlighter';

export interface CodeDisplayLine {
  lineNum: number;
  text: string;
  html: string;
}

const EMPTY_CODE_LINE_HTML = '&nbsp;';
const LONG_LINE_THRESHOLD = 180;

const STRUCTURED_TEXT_MARKER =
  /\b(PROGRAM|FUNCTION_BLOCK|FUNCTION|ORGANIZATION_BLOCK|DATA_BLOCK|VAR|VAR_INPUT|VAR_OUTPUT|VAR_IN_OUT|END_VAR|BEGIN|IF|THEN|CASE|END_IF|END_CASE|END_PROGRAM|END_FUNCTION_BLOCK|TON|TOF|R_TRIG|F_TRIG)\b|:=|%[IQM]/i;

const BLOCK_BOUNDARY_KEYWORDS = [
  'FUNCTION_BLOCK',
  'ORGANIZATION_BLOCK',
  'DATA_BLOCK',
  'PROGRAM',
  'FUNCTION',
  'VAR_INPUT',
  'VAR_OUTPUT',
  'VAR_IN_OUT',
  'VAR_GLOBAL',
  'VAR_TEMP',
  'VAR_STAT',
  'VAR_EXTERNAL',
  'VAR',
  'END_VAR',
  'BEGIN',
  'REGION',
  'END_REGION',
  'IF',
  'ELSIF',
  'ELSE',
  'END_IF',
  'CASE',
  'END_CASE',
  'FOR',
  'END_FOR',
  'WHILE',
  'END_WHILE',
  'REPEAT',
  'END_REPEAT',
  'END_FUNCTION_BLOCK',
  'END_PROGRAM',
  'END_FUNCTION',
  'END_ORGANIZATION_BLOCK',
  'END_DATA_BLOCK',
].join('|');

const CODE_BOUNDARY =
  `(?:#?"?[A-Za-z_][\\w"]*\\s*:=|#?"?[A-Za-z_][\\w"]*\\s*\\(|\\d+\\s*:|\\b(?:${BLOCK_BOUNDARY_KEYWORDS})\\b)`;

function normalizeLineEndings(code: string): string {
  return code.replace(/\r\n?/g, '\n');
}

function decodeEscapedLineEndingsIfNeeded(code: string): string {
  if (code.includes('\n')) return code;
  const escapedLineBreaks = code.match(/\\r\\n|\\n|\\r/g);
  if (!escapedLineBreaks || escapedLineBreaks.length < 2) return code;
  return code
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\\t/g, '  ');
}

/** The closing mark of a block comment still open at the end of a line. */
type OpenComment = '*)' | '*/';

/**
 * One line with its comments and single-quoted strings lifted out, so keyword
 * tests, line splitting and indentation only ever see code. A comment that
 * mentions IF, VAR or PROGRAM is prose: read as code, it was split into fake
 * statements and indented everything after it.
 */
interface ScannedLine {
  /** The line with each comment and single-quoted string replaced by a placeholder. */
  masked: string;
  literals: string[];
  /** Where a comment carried in from the previous line ends; 0 when none was. */
  carriedCommentEnd: number;
  /** Where a comment carried on to the next line starts; the line length when none is. */
  openCommentStart: number;
  open: OpenComment | null;
}

// One private-use character per lifted literal; no pattern in this file can match one.
const PLACEHOLDER_BASE = 0xE100;
const PLACEHOLDER = new RegExp(`[${String.fromCharCode(PLACEHOLDER_BASE)}-${String.fromCharCode(0xF8FF)}]`, 'g');

/** Index just past the string literal opening at `start`; `$` escapes the next character, as IEC 61131-3 defines. */
function stringLiteralEnd(line: string, start: number): number {
  const quote = line[start];
  for (let i = start + 1; i < line.length; i += 1) {
    if (line[i] === '$') i += 1;
    else if (line[i] === quote) return i + 1;
  }
  return line.length;
}

function scanLine(line: string, carriedIn: OpenComment | null): ScannedLine {
  const literals: string[] = [];
  let masked = '';
  let i = 0;
  let open = carriedIn;
  let carriedCommentEnd = 0;
  let openCommentStart = line.length;
  const lift = (start: number, end: number) => {
    masked += String.fromCharCode(PLACEHOLDER_BASE + literals.length);
    literals.push(line.slice(start, end));
    i = end;
  };

  if (open) {
    const close = line.indexOf(open);
    carriedCommentEnd = close < 0 ? line.length : close + open.length;
    if (close >= 0) open = null;
    lift(0, carriedCommentEnd);
  }
  while (i < line.length) {
    const pair = line.slice(i, i + 2);
    if (pair === '//') {
      lift(i, line.length);
    } else if (pair === '(*' || pair === '/*') {
      const closing: OpenComment = pair === '(*' ? '*)' : '*/';
      const close = line.indexOf(closing, i + 2);
      if (close < 0) {
        open = closing;
        openCommentStart = i;
        lift(i, line.length);
      } else {
        lift(i, close + closing.length);
      }
    } else if (line[i] === "'") {
      lift(i, stringLiteralEnd(line, i));
    } else if (line[i] === '"') {
      // A WSTRING in IEC but a tag name in Siemens SCL, which the splitter has
      // to see; it is only stepped over, so that no comment starts inside it.
      const end = stringLiteralEnd(line, i);
      masked += line.slice(i, end);
      i = end;
    } else {
      masked += line[i];
      i += 1;
    }
  }
  return { masked, literals, carriedCommentEnd, openCommentStart, open };
}

function unmask(text: string, literals: string[]): string {
  return text.replace(PLACEHOLDER, mark => literals[mark.charCodeAt(0) - PLACEHOLDER_BASE] ?? mark);
}

function codeOf(scanned: ScannedLine): string {
  return scanned.masked.replace(PLACEHOLDER, ' ').trim();
}

function leadingWhitespaceLength(line: string): number {
  return line.length - line.trimStart().length;
}

function shouldFormatCompactLine(line: string): boolean {
  const trimmed = line.trim();
  if (!STRUCTURED_TEXT_MARKER.test(trimmed)) return false;
  const boundaryCount = (trimmed.match(new RegExp(CODE_BOUNDARY, 'gi')) || []).length;
  return trimmed.length >= LONG_LINE_THRESHOLD
    || boundaryCount >= 3
    || (boundaryCount >= 2 && /;\s+\S/.test(trimmed));
}

function splitCompactStructuredTextLine(line: string): string[] {
  let formatted = line.trim();

  formatted = formatted.replace(new RegExp(`;\\s+(?=${CODE_BOUNDARY})`, 'gi'), ';\n');
  formatted = formatted.replace(new RegExp(`\\s+(?=\\b(?:${BLOCK_BOUNDARY_KEYWORDS})\\b)`, 'gi'), '\n');
  formatted = formatted.replace(new RegExp(`\\b(THEN|DO|OF|ELSE)\\b\\s+(?=${CODE_BOUNDARY})`, 'gi'), '$1\n');
  formatted = formatted.replace(/\n{3,}/g, '\n\n');

  return formatted
    .split('\n')
    .map(part => part.trim())
    .filter((part, index, parts) => part.length > 0 || (index > 0 && index < parts.length - 1));
}

function decreasesIndentBefore(line: string): boolean {
  return /^(END_VAR|END_IF|END_CASE|END_FOR|END_WHILE|END_REPEAT|END_FUNCTION_BLOCK|END_PROGRAM|END_FUNCTION|END_ORGANIZATION_BLOCK|END_DATA_BLOCK|END_REGION|END_STRUCT|END_TYPE)\b/i.test(line)
    || /^(ELSE|ELSIF|UNTIL)\b/i.test(line)
    || /^\)/.test(line);
}

function increasesIndentAfter(line: string): boolean {
  if (/^(END_|UNTIL\b)/i.test(line)) return false;
  return /\b(VAR|VAR_INPUT|VAR_OUTPUT|VAR_IN_OUT|VAR_GLOBAL|VAR_TEMP|VAR_STAT|VAR_EXTERNAL|PROGRAM|FUNCTION_BLOCK|FUNCTION|ORGANIZATION_BLOCK|DATA_BLOCK|REGION|STRUCT)\b/i.test(line)
    || /\b(IF|ELSIF)\b[\s\S]*\bTHEN\b/i.test(line)
    || /\b(CASE)\b[\s\S]*\bOF\b/i.test(line)
    || /\b(FOR|WHILE)\b[\s\S]*\bDO\b/i.test(line)
    || /^(ELSE|REPEAT|TYPE)\b/i.test(line)
    // Enum bodies (TYPE … END_TYPE) open with a bare "(" and close with ");".
    || /^\($/.test(line);
}

function indentStructuredTextLines(lines: string[]): string[] {
  let indent = 0;
  let open: OpenComment | null = null;
  let commentIndent = '';
  let commentOffset = 0;
  return lines.map(rawLine => {
    const insideComment = open !== null;
    const scanned = scanLine(rawLine, open);
    open = scanned.open;
    if (insideComment) {
      // The body of a comment that spans lines keeps the author's layout,
      // moved to wherever its opening line now sits.
      if (!rawLine.trim()) return '';
      return `${commentIndent}${rawLine.slice(Math.min(commentOffset, leadingWhitespaceLength(rawLine))).trimEnd()}`;
    }
    const line = rawLine.trim();
    if (!line) return '';
    const code = codeOf(scanned);
    if (decreasesIndentBefore(code)) {
      indent = Math.max(0, indent - 1);
    }
    const prefix = '  '.repeat(indent);
    if (increasesIndentAfter(code)) {
      indent += 1;
    }
    if (open) {
      commentIndent = prefix;
      commentOffset = leadingWhitespaceLength(rawLine);
    }
    return `${prefix}${line}`;
  });
}

function formatCompactStructuredText(code: string): string {
  const originalLines = code.split('\n');
  let changed = false;
  const expandedLines: string[] = [];
  let open: OpenComment | null = null;

  for (const line of originalLines) {
    const scanned = scanLine(line, open);
    open = scanned.open;
    if (shouldFormatCompactLine(scanned.masked)) {
      const splitLines = splitCompactStructuredTextLine(scanned.masked).map(part => unmask(part, scanned.literals));
      expandedLines.push(...splitLines);
      changed ||= splitLines.length > 1;
    } else {
      expandedLines.push(line.replace(/\s+$/g, ''));
    }
  }

  return indentStructuredTextLines(expandedLines).join('\n');
}

/**
 * Only the SCL between ladder blocks is laid out; each ladder block keeps its
 * lines as written. The ST layout re-indented rungs and split long ones, the
 * chat download is this text, and a NETWORK title must stay on its line.
 */
function formatAroundLadderBlocks(code: string): string {
  const lines = code.split('\n');
  const parts: string[] = [];
  let next = 0;
  const layOut = (from: number, to: number) => {
    if (to > from) parts.push(formatCompactStructuredText(lines.slice(from, to).join('\n')));
  };
  for (const block of listLadBlocks(code)) {
    layOut(next, block.startLine - 1);
    parts.push(lines.slice(block.startLine - 1, block.endLine).join('\n'));
    next = block.endLine;
  }
  layOut(next, lines.length);
  return parts.join('\n');
}

export function normalizeCodeForDisplay(code: string): string {
  const normalized = normalizeLineEndings(decodeEscapedLineEndingsIfNeeded(code)).replace(/\u00a0/g, ' ');
  if (!normalized.trim()) return normalized;
  if (containsLadderBlocks(normalized)) return formatAroundLadderBlocks(normalized);
  return formatCompactStructuredText(normalized);
}

export function getDisplayCodeLineCount(code: string): number {
  const displayCode = normalizeCodeForDisplay(code);
  return displayCode.split('\n').length;
}

/** The highlighter reads one line at a time, so the parts of a comment that spans lines are marked here. */
function highlightDisplayLine(line: string, scanned: ScannedLine): string {
  const { carriedCommentEnd: codeStart, openCommentStart: codeEnd } = scanned;
  if (codeStart === 0 && codeEnd === line.length) return highlightSTCode(line);
  return highlightSTComment(line.slice(0, codeStart))
    + (codeEnd > codeStart ? highlightSTCode(line.slice(codeStart, codeEnd)) : '')
    + highlightSTComment(line.slice(codeEnd));
}

export function getCodeDisplayLines(code: string): CodeDisplayLine[] {
  const displayCode = normalizeCodeForDisplay(code);
  let open: OpenComment | null = null;
  return displayCode.split('\n').map((line, index) => {
    const scanned = scanLine(line, open);
    open = scanned.open;
    return {
      lineNum: index + 1,
      text: line,
      html: line ? highlightDisplayLine(line, scanned) : EMPTY_CODE_LINE_HTML,
    };
  });
}
